import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MessageSquare, Clock, CheckCircle, AlertCircle, Send, User, Bot, ShieldCheck, Sparkles, TrendingUp, Target, Loader2, ThumbsUp, ThumbsDown, DollarSign, Globe, Languages, Brain, Lightbulb, ArrowRight, XCircle, Archive, Edit3, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';
import { AIControlledDraft, NegotiationBands } from '@/components/AIControlledDrafts';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const CLOSE_REASONS = [
  { value: 'deal_completed', label: 'Deal Completed Successfully', icon: '✅' },
  { value: 'not_interested', label: 'Not Interested', icon: '❌' },
  { value: 'found_another', label: 'Found Another Supplier/Buyer', icon: '🔄' },
  { value: 'price_mismatch', label: 'Price Did Not Match', icon: '💰' },
  { value: 'quality_concerns', label: 'Quality Concerns', icon: '⚠️' },
  { value: 'no_response', label: 'No Response from Other Party', icon: '🔇' },
  { value: 'other', label: 'Other Reason', icon: '📝' }
];

export default function Inquiries() {
  const { language, t } = useLanguage();
  const [inquiries, setInquiries] = useState([]);
  const [user, setUser] = useState(null);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showConversation, setShowConversation] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  
  // Filter state - Active vs Closed
  const [statusFilter, setStatusFilter] = useState('active'); // 'active', 'closed', 'all'
  
  // AI Negotiation Assistant state
  const [showNegotiationAssist, setShowNegotiationAssist] = useState(false);
  const [negotiationSuggestions, setNegotiationSuggestions] = useState(null);
  const [isLoadingAssist, setIsLoadingAssist] = useState(false);
  const [isApprovingResponse, setIsApprovingResponse] = useState(false);
  
  // New AI Controlled Draft mode
  const [aiMode, setAiMode] = useState('draft'); // 'draft' | 'negotiate' | 'legacy'
  const [showControlledDraft, setShowControlledDraft] = useState(false);
  
  // Close conversation state
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [closeReason, setCloseReason] = useState('');
  const [closeNote, setCloseNote] = useState('');
  const [isClosing, setIsClosing] = useState(false);
  
  // Translation state for messages
  const [translatedMessages, setTranslatedMessages] = useState({});
  const [isTranslatingMessages, setIsTranslatingMessages] = useState({});

  const fetchInquiries = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/inquiries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Sort by most recent message/activity first (newest at top)
      const sortedInquiries = response.data.sort((a, b) => {
        const dateA = new Date(a.last_message_at || a.created_at);
        const dateB = new Date(b.last_message_at || b.created_at);
        return dateB - dateA;
      });
      setInquiries(sortedInquiries);
    } catch (error) {
      toast.error('Failed to fetch inquiries');
    }
  }, []);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    setUser(userData);
    fetchInquiries();
  }, [fetchInquiries]);

  // Filter inquiries based on status - memoized for performance
  const filteredInquiries = useMemo(() => {
    return inquiries.filter(inquiry => {
      if (statusFilter === 'all') return true;
      const isClosed = inquiry.status === 'closed' || inquiry.status === 'won' || inquiry.status === 'lost';
      return statusFilter === 'closed' ? isClosed : !isClosed;
    });
  }, [inquiries, statusFilter]);

  // Get counts for tabs - memoized
  const { activeCount, closedCount } = useMemo(() => ({
    activeCount: inquiries.filter(i => !['closed', 'won', 'lost'].includes(i.status)).length,
    closedCount: inquiries.filter(i => ['closed', 'won', 'lost'].includes(i.status)).length
  }), [inquiries]);

  const fetchInquiryDetails = async (inquiryId) => {
    try {
      const response = await axios.get(`${API}/inquiries/${inquiryId}`);
      setSelectedInquiry(response.data);
    } catch (error) {
      toast.error('Failed to fetch inquiry details');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedInquiry) return;

    setIsSending(true);
    try {
      await axios.post(`${API}/inquiries/${selectedInquiry.id}/messages`, {
        message: newMessage.trim()
      });
      toast.success('Message sent');
      setNewMessage('');
      // Refresh inquiry details
      await fetchInquiryDetails(selectedInquiry.id);
      // Also refresh the list
      await fetchInquiries();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  const openConversation = async (inquiry) => {
    await fetchInquiryDetails(inquiry.id);
    setShowConversation(true);
    setNegotiationSuggestions(null); // Reset suggestions when opening new conversation
  };

  // AI Negotiation Assistant Functions
  const [replyStyle, setReplyStyle] = useState('professional');

  const requestNegotiationAssist = async () => {
    if (!selectedInquiry) return;
    
    // Check if inquiry is closed
    if (selectedInquiry.status === 'closed' || selectedInquiry.status === 'won' || selectedInquiry.status === 'lost') {
      toast.info('This conversation is closed. No AI suggestions available.');
      return;
    }
    
    setIsLoadingAssist(true);
    try {
      const token = localStorage.getItem('token');
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await axios.post(`${API}/ai/negotiation-assist`, {
        inquiry_id: selectedInquiry.id,
        product_id: selectedInquiry.product_id,
        conversation_history: selectedInquiry.messages || [],
        buyer_requirements: {
          quantity: selectedInquiry.quantity,
          unit: selectedInquiry.unit,
          initial_message: selectedInquiry.message
        },
        current_stage: "negotiation",
        deal_status: selectedInquiry.status || "open",
        reply_style: replyStyle,
        seller_name: userData.full_name?.split(' ')[0] || 'there',
        company_name: userData.company_name || 'our company'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNegotiationSuggestions(response.data);
      setShowNegotiationAssist(true);
    } catch (error) {
      toast.error('Failed to get AI suggestions');
    } finally {
      setIsLoadingAssist(false);
    }
  };

  const approveAndSendResponse = async (responseText) => {
    if (!selectedInquiry || !responseText) return;
    
    setIsApprovingResponse(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/ai/approve-negotiation-response`, null, {
        params: {
          inquiry_id: selectedInquiry.id,
          response_text: responseText
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('AI-suggested response sent!');
      setShowNegotiationAssist(false);
      setNegotiationSuggestions(null);
      await fetchInquiryDetails(selectedInquiry.id);
      await fetchInquiries();
    } catch (error) {
      toast.error('Failed to send response');
    } finally {
      setIsApprovingResponse(false);
    }
  };

  const copyToMessageBox = (text) => {
    setNewMessage(text);
    setShowNegotiationAssist(false);
    toast.info('Suggestion copied to message box. Edit and send when ready.');
  };

  // Close conversation handler
  const handleCloseConversation = async () => {
    if (!closeReason) {
      toast.error('Please select a reason for closing');
      return;
    }

    setIsClosing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/inquiries/${selectedInquiry.id}/close`, {
        reason: closeReason,
        note: closeNote
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Conversation closed successfully');
      setShowCloseDialog(false);
      setShowConversation(false);
      setCloseReason('');
      setCloseNote('');
      await fetchInquiries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close conversation');
    } finally {
      setIsClosing(false);
    }
  };

  // Translate messages when language changes or new inquiry is selected
  useEffect(() => {
    const translateMessages = async () => {
      if (!selectedInquiry?.messages || selectedInquiry.messages.length === 0) return;
      
      const currentLang = language || 'en';
      if (currentLang === 'en') {
        setTranslatedMessages({});
        return;
      }

      for (const msg of selectedInquiry.messages) {
        const msgKey = `${msg.id || msg.created_at}_${currentLang}`;
        if (!msg.message || translatedMessages[msgKey]) continue;
        
        setIsTranslatingMessages(prev => ({ ...prev, [msg.id || msg.created_at]: true }));
        
        try {
          const response = await axios.post(`${API}/ai/translate`, {
            text: msg.message,
            target_language: currentLang,
            context: 'conversation_message'
          });
          
          setTranslatedMessages(prev => ({
            ...prev,
            [msgKey]: response.data.translated_text
          }));
        } catch (error) {
          console.error('Translation failed for message:', msg.id);
        } finally {
          setIsTranslatingMessages(prev => ({ ...prev, [msg.id || msg.created_at]: false }));
        }
      }
    };

    if (showConversation && selectedInquiry) {
      translateMessages();
    }
  }, [language, selectedInquiry, showConversation]);

  // Helper function to get translated message
  const getTranslatedMessage = (msg) => {
    const currentLang = language || 'en';
    if (currentLang === 'en') return msg.message;
    
    const msgKey = `${msg.id || msg.created_at}_${currentLang}`;
    return translatedMessages[msgKey] || msg.message;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-amber-600" />;
      case 'responded':
      case 'active':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'closed':
        return <AlertCircle className="h-4 w-4 text-slate-400" />;
      default:
        return <MessageSquare className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'responded':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'active':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'closed':
        return 'bg-slate-100 text-slate-800 border-slate-200';
      default:
        return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="inquiries-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-primary mb-2">
            {user?.role === 'buyer' ? 'My Inquiries' : 'Received Inquiries'}
          </h1>
          <p className="text-muted-foreground">
            {user?.role === 'buyer' 
              ? 'Track your product inquiries and continue negotiations' 
              : 'View and respond to buyer inquiries'}
          </p>
        </div>

        {/* Filter Tabs - Active / Closed / All */}
        <div className="flex items-center gap-2 mb-6 border-b pb-4">
          <Button
            variant={statusFilter === 'active' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('active')}
            className="gap-2"
            data-testid="filter-active-btn"
          >
            <CheckCircle className="h-4 w-4" />
            Active
            {activeCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                {activeCount}
              </Badge>
            )}
          </Button>
          <Button
            variant={statusFilter === 'closed' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('closed')}
            className="gap-2"
            data-testid="filter-closed-btn"
          >
            <Archive className="h-4 w-4" />
            Closed
            {closedCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                {closedCount}
              </Badge>
            )}
          </Button>
          <Button
            variant={statusFilter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('all')}
            data-testid="filter-all-btn"
          >
            All ({inquiries.length})
          </Button>
        </div>

        <div className="space-y-4">
          {filteredInquiries.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-lg font-medium text-muted-foreground">
                  {statusFilter === 'closed' 
                    ? 'No closed inquiries' 
                    : statusFilter === 'active'
                      ? (user?.role === 'buyer' ? 'No active inquiries' : 'No active inquiries')
                      : (user?.role === 'buyer' ? 'No inquiries sent yet' : 'No inquiries received yet')}
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  {statusFilter === 'all' && (user?.role === 'buyer' 
                    ? 'Browse products and send inquiries to get started' 
                    : 'Add products to start receiving inquiries')}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredInquiries.map((inquiry) => (
              <Card key={inquiry.id} className="inquiry-item hover:shadow-md transition-shadow cursor-pointer" data-testid={`inquiry-card-${inquiry.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="font-heading text-xl mb-1">{inquiry.product_title}</CardTitle>
                      <CardDescription>
                        {user?.role === 'buyer' 
                          ? `To: ${inquiry.seller_name}` 
                          : `From: ${inquiry.buyer_name} (${inquiry.buyer_email})`}
                      </CardDescription>
                    </div>
                    <Badge className={getStatusColor(inquiry.status)} data-testid={`inquiry-status-${inquiry.id}`}>
                      <span className="flex items-center gap-1">
                        {getStatusIcon(inquiry.status)}
                        {inquiry.status}
                      </span>
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Initial Message:</p>
                      <p className="text-sm bg-slate-50 p-3 rounded-lg line-clamp-2">{inquiry.message}</p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Quantity: <span className="font-medium text-foreground">{inquiry.quantity}</span>
                      </p>
                    </div>

                    {inquiry.ai_response && (
                      <div className="flex items-center gap-2 text-sm text-green-600">
                        <Bot className="h-4 w-4" />
                        AI response generated
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-2">
                      <p className="text-xs text-muted-foreground">
                        {formatDate(inquiry.created_at)}
                        {inquiry.messages && inquiry.messages.length > 0 && (
                          <span className="ml-2">• {inquiry.messages.length} messages</span>
                        )}
                      </p>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => openConversation(inquiry)}
                        data-testid={`open-conversation-${inquiry.id}`}
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        View Conversation
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Conversation Dialog */}
        <Dialog open={showConversation} onOpenChange={setShowConversation}>
          <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle className="font-heading">
                    {selectedInquiry?.product_title}
                  </DialogTitle>
                  <DialogDescription>
                    {user?.role === 'buyer' 
                      ? `Conversation with ${selectedInquiry?.seller_name}` 
                      : `Conversation with ${selectedInquiry?.buyer_name}`}
                  </DialogDescription>
                </div>
                {language !== 'en' && (
                  <Badge variant="outline" className="flex items-center gap-1">
                    <Languages className="h-3 w-3" />
                    {language === 'hi' ? 'Hindi' : language === 'gu' ? 'Gujarati' : language.toUpperCase()}
                  </Badge>
                )}
              </div>
            </DialogHeader>
            
            {/* Messages */}
            <div className="flex-1 overflow-y-auto max-h-96 space-y-4 py-4">
              {selectedInquiry?.messages?.map((msg, idx) => (
                <div 
                  key={msg.id || idx}
                  className={`flex gap-3 ${msg.sender_role === 'ai' ? 'justify-center' : 
                    (msg.sender_id === user?.id ? 'justify-end' : 'justify-start')}`}
                >
                  {msg.sender_role === 'ai' ? (
                    <div className="max-w-[90%] bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Bot className="h-4 w-4 text-indigo-600" />
                        <span className="text-xs font-medium text-indigo-600">AI Assistant</span>
                        <span className="text-xs text-muted-foreground">{formatDate(msg.created_at)}</span>
                      </div>
                      {isTranslatingMessages[msg.id || msg.created_at] ? (
                        <p className="text-sm flex items-center gap-1 text-muted-foreground">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Translating...
                        </p>
                      ) : (
                        <p className="text-sm">{getTranslatedMessage(msg)}</p>
                      )}
                    </div>
                  ) : (
                    <div className={`max-w-[75%] rounded-lg p-3 ${
                      msg.sender_id === user?.id 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-slate-100'
                    }`}>
                      <div className="flex items-center gap-2 mb-1">
                        <User className="h-3 w-3" />
                        <span className="text-xs font-medium">{msg.sender_name}</span>
                        <Badge variant="outline" className="text-xs py-0">{msg.sender_role}</Badge>
                      </div>
                      {isTranslatingMessages[msg.id || msg.created_at] ? (
                        <p className="text-sm flex items-center gap-1">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Translating...
                        </p>
                      ) : (
                        <p className="text-sm">{getTranslatedMessage(msg)}</p>
                      )}
                      {/* Display attached images */}
                      {msg.images && msg.images.length > 0 && (
                        <div className="flex gap-2 mt-2 flex-wrap">
                          {msg.images.map((img, imgIdx) => (
                            <a 
                              key={imgIdx} 
                              href={img.startsWith('data:') ? img : `${BACKEND_URL}${img}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <img 
                                src={img.startsWith('data:') ? img : `${BACKEND_URL}${img}`}
                                alt={`Attachment ${imgIdx + 1}`}
                                className="w-16 h-16 object-cover rounded border hover:opacity-80 cursor-pointer"
                              />
                            </a>
                          ))}
                        </div>
                      )}
                      <p className={`text-xs mt-1 ${msg.sender_id === user?.id ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                        {formatDate(msg.created_at)}
                      </p>
                    </div>
                  )}
                </div>
              ))}
              
              {(!selectedInquiry?.messages || selectedInquiry.messages.length === 0) && (
                <div className="text-center text-muted-foreground py-8">
                  <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No messages yet. Start the conversation!</p>
                </div>
              )}
            </div>

            <Separator />

            {/* AI Tools for Sellers */}
            {user?.role === 'seller' && selectedInquiry?.status !== 'closed' && (
              <div className="space-y-2 pt-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-purple-600 flex items-center gap-1">
                    <Sparkles className="h-3 w-3" /> AI Tools
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowControlledDraft(true)}
                    className="gap-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                    data-testid="ai-draft-btn"
                  >
                    <Edit3 className="h-4 w-4" />
                    AI Draft
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setAiMode('negotiate');
                      setShowNegotiationAssist(true);
                      setNegotiationSuggestions(null);
                    }}
                    className="gap-1 text-purple-600 border-purple-200 hover:bg-purple-50"
                    data-testid="ai-negotiate-btn"
                  >
                    <Shield className="h-4 w-4" />
                    Strategy Bands
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={requestNegotiationAssist}
                    disabled={isLoadingAssist}
                    className="gap-1 text-slate-500"
                    data-testid="ai-legacy-btn"
                  >
                    {isLoadingAssist ? (
                      <><Loader2 className="h-4 w-4 animate-spin" /> Analyzing...</>
                    ) : (
                      <><Brain className="h-4 w-4" /> Quick Analysis</>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Draft: Controlled responses • Bands: Negotiation strategies • Quick: Market insights
                </p>
              </div>
            )}

            {/* Message Input */}
            {selectedInquiry?.status !== 'closed' ? (
              <form onSubmit={handleSendMessage} className="flex gap-2 pt-2">
                <Textarea
                  placeholder="Type your message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  className="flex-1 min-h-[60px] max-h-[120px]"
                  data-testid="message-input"
                />
                <div className="flex flex-col gap-2 self-end">
                  <Button 
                    type="submit" 
                    disabled={isSending || !newMessage.trim()}
                    data-testid="send-message-btn"
                  >
                    {isSending ? (
                      'Sending...'
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send
                      </>
                    )}
                  </Button>
                  <Button 
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowCloseDialog(true)}
                    className="text-destructive hover:text-destructive"
                    data-testid="close-conversation-btn"
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Close
                  </Button>
                </div>
              </form>
            ) : (
              <div className="bg-slate-100 rounded-lg p-4 text-center">
                <Archive className="h-6 w-6 mx-auto mb-2 text-slate-400" />
                <p className="text-sm font-medium text-slate-600">This conversation has been closed</p>
                {selectedInquiry?.close_reason && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Reason: {CLOSE_REASONS.find(r => r.value === selectedInquiry.close_reason)?.label || selectedInquiry.close_reason}
                  </p>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Close Conversation Dialog */}
        <Dialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-destructive" />
                Close Conversation
              </DialogTitle>
              <DialogDescription>
                Please select a reason for closing this conversation. This helps us improve the platform.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="close-reason">Reason *</Label>
                <Select value={closeReason} onValueChange={setCloseReason}>
                  <SelectTrigger id="close-reason" data-testid="close-reason-select">
                    <SelectValue placeholder="Select a reason..." />
                  </SelectTrigger>
                  <SelectContent>
                    {CLOSE_REASONS.map((reason) => (
                      <SelectItem key={reason.value} value={reason.value}>
                        <span className="flex items-center gap-2">
                          <span>{reason.icon}</span>
                          <span>{reason.label}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="close-note">Additional Notes (Optional)</Label>
                <Textarea
                  id="close-note"
                  placeholder="Any additional feedback or details..."
                  value={closeNote}
                  onChange={(e) => setCloseNote(e.target.value)}
                  className="min-h-[80px]"
                  data-testid="close-note-input"
                />
              </div>

              {closeReason === 'deal_completed' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800">
                    🎉 Congratulations on completing the deal! We'd love to hear about your experience.
                  </p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCloseDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCloseConversation}
                disabled={isClosing || !closeReason}
                variant="destructive"
                data-testid="confirm-close-btn"
              >
                {isClosing ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Closing...</>
                ) : (
                  <><XCircle className="h-4 w-4 mr-2" /> Close Conversation</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* AI Negotiation Assistant Dialog */}
        <Dialog open={showNegotiationAssist} onOpenChange={setShowNegotiationAssist}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Brain className="h-5 w-5 text-purple-600" />
                AI Negotiation Assistant
              </DialogTitle>
              <DialogDescription>
                Market-based insights and strategy suggestions for your negotiation
              </DialogDescription>
            </DialogHeader>

            {/* Reply Style Selector - Only show when no suggestions yet */}
            {!negotiationSuggestions && (
              <div className="flex items-center gap-2 py-2 border-b">
                <span className="text-sm text-muted-foreground">Tone:</span>
                <div className="flex gap-1">
                  {['professional', 'friendly', 'concise'].map((style) => (
                    <Button
                      key={style}
                      size="sm"
                      variant={replyStyle === style ? 'default' : 'outline'}
                      onClick={() => setReplyStyle(style)}
                      className="capitalize text-xs"
                      data-testid={`style-${style}-btn`}
                    >
                      {style}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Closed Deal Notice */}
            {negotiationSuggestions?.status === 'closed' && (
              <div className="bg-slate-100 rounded-lg p-4 text-center">
                <XCircle className="h-8 w-8 mx-auto mb-2 text-slate-400" />
                <p className="font-medium">{negotiationSuggestions.message}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Deal Status: <Badge variant="secondary">{negotiationSuggestions.deal_status}</Badge>
                </p>
              </div>
            )}
            
            {negotiationSuggestions && negotiationSuggestions.status !== 'closed' ? (
              <div className="space-y-4">
                {/* Analysis Section */}
                {negotiationSuggestions.analysis && (
                  <div className="bg-slate-50 rounded-lg p-4 border">
                    <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                      <Target className="h-4 w-4 text-blue-600" />
                      Deal Analysis
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-muted-foreground">Buyer Intent</p>
                        <p className="font-medium">{negotiationSuggestions.analysis.buyer_intent}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Deal Potential</p>
                        <Badge className={
                          negotiationSuggestions.analysis.deal_potential === 'High' ? 'bg-green-100 text-green-800' :
                          negotiationSuggestions.analysis.deal_potential === 'Medium' ? 'bg-amber-100 text-amber-800' :
                          'bg-red-100 text-red-800'
                        }>
                          {negotiationSuggestions.analysis.deal_potential}
                        </Badge>
                      </div>
                    </div>
                    {negotiationSuggestions.analysis.key_concerns?.length > 0 && (
                      <div className="mt-3">
                        <p className="text-muted-foreground text-xs mb-1">Key Concerns</p>
                        <ul className="text-sm space-y-1">
                          {negotiationSuggestions.analysis.key_concerns.map((concern, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <AlertCircle className="h-3 w-3 text-amber-500 mt-1 flex-shrink-0" />
                              <span>{concern}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Counter Offer Section */}
                {negotiationSuggestions.counter_offer && (
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                      <DollarSign className="h-4 w-4 text-green-600" />
                      Suggested Counter-Offer
                    </h4>
                    <div className="grid grid-cols-3 gap-3 text-sm mb-3">
                      <div>
                        <p className="text-muted-foreground">Price</p>
                        <p className="font-semibold text-green-700">{negotiationSuggestions.counter_offer.suggested_price}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Quantity</p>
                        <p className="font-medium">{negotiationSuggestions.counter_offer.suggested_quantity}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Terms</p>
                        <p className="font-medium">{negotiationSuggestions.counter_offer.suggested_terms}</p>
                      </div>
                    </div>
                    <p className="text-xs text-green-700 bg-green-100 p-2 rounded">
                      <Lightbulb className="h-3 w-3 inline mr-1" />
                      {negotiationSuggestions.counter_offer.reasoning}
                    </p>
                  </div>
                )}

                {/* Market Context Section */}
                {negotiationSuggestions.market_context && (
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                      <Globe className="h-4 w-4 text-blue-600" />
                      Market Context
                    </h4>
                    <div className="space-y-2 text-sm">
                      {negotiationSuggestions.market_context.price_comparison && (
                        <div>
                          <p className="text-muted-foreground text-xs">Price Comparison</p>
                          <p>{negotiationSuggestions.market_context.price_comparison}</p>
                        </div>
                      )}
                      {negotiationSuggestions.market_context.demand_level && (
                        <div>
                          <p className="text-muted-foreground text-xs">Demand Level</p>
                          <p>{negotiationSuggestions.market_context.demand_level}</p>
                        </div>
                      )}
                      {negotiationSuggestions.market_context.timing && (
                        <div>
                          <p className="text-muted-foreground text-xs">Timing</p>
                          <p>{negotiationSuggestions.market_context.timing}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Market Insights Section */}
                {negotiationSuggestions.market_insights && (
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                      <TrendingUp className="h-4 w-4 text-blue-600" />
                      Market Insights
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div>
                        <p className="text-muted-foreground">Price Comparison</p>
                        <p>{negotiationSuggestions.market_insights.price_comparison}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Demand Outlook</p>
                        <p>{negotiationSuggestions.market_insights.demand_outlook}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Regional Opportunity</p>
                        <p>{negotiationSuggestions.market_insights.regional_opportunity}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* AI Suggestions List */}
                {negotiationSuggestions.suggestions?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm">Recommendations</h4>
                    {negotiationSuggestions.suggestions.map((suggestion, idx) => (
                      <div key={idx} className={`p-3 rounded-lg border ${
                        suggestion.type === 'warning' ? 'bg-red-50 border-red-200' :
                        suggestion.type === 'counter_offer' ? 'bg-green-50 border-green-200' :
                        'bg-purple-50 border-purple-200'
                      }`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm">{suggestion.title}</span>
                          <Badge variant="outline" className="text-xs">
                            {suggestion.priority}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{suggestion.content}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Risk Notes Section */}
                {negotiationSuggestions.risk_notes && (
                  <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                    <h4 className="font-semibold text-sm flex items-center gap-2 mb-2 text-amber-700">
                      <AlertCircle className="h-4 w-4" />
                      Risk Analysis
                    </h4>
                    <div className="space-y-2 text-sm">
                      {negotiationSuggestions.risk_notes.too_high && (
                        <p className="text-amber-700">
                          <span className="font-medium">↑ Too High:</span> {negotiationSuggestions.risk_notes.too_high}
                        </p>
                      )}
                      {negotiationSuggestions.risk_notes.too_low && (
                        <p className="text-amber-700">
                          <span className="font-medium">↓ Too Low:</span> {negotiationSuggestions.risk_notes.too_low}
                        </p>
                      )}
                      {negotiationSuggestions.risk_notes.recommendation && (
                        <p className="text-green-700 font-medium mt-2">
                          ✓ Recommendation: {negotiationSuggestions.risk_notes.recommendation}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Warnings Section */}
                {negotiationSuggestions.warnings?.length > 0 && (
                  <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                    <h4 className="font-semibold text-sm flex items-center gap-2 mb-2 text-red-700">
                      <AlertCircle className="h-4 w-4" />
                      Warnings
                    </h4>
                    <ul className="text-sm space-y-1">
                      {negotiationSuggestions.warnings.map((warning, idx) => (
                        <li key={idx} className="text-red-600">{warning}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Negotiation Strategies - Main Feature */}
                {negotiationSuggestions.strategies?.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-purple-600" />
                      Select Your Negotiation Strategy
                    </h4>
                    <p className="text-xs text-muted-foreground mb-3">
                      Choose a strategy based on your priorities. Each includes a ready-to-send message.
                    </p>
                    {negotiationSuggestions.strategies.map((strategy, idx) => (
                      <div key={idx} className={`rounded-lg p-4 border ${
                        strategy.name === 'Conservative' ? 'bg-blue-50 border-blue-200' :
                        strategy.name === 'Competitive' ? 'bg-green-50 border-green-200' :
                        'bg-amber-50 border-amber-200'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm flex items-center gap-2">
                            {strategy.name === 'Conservative' && <Target className="h-4 w-4 text-blue-600" />}
                            {strategy.name === 'Competitive' && <TrendingUp className="h-4 w-4 text-green-600" />}
                            {strategy.name === 'Profit-Focused' && <DollarSign className="h-4 w-4 text-amber-600" />}
                            {strategy.name}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {strategy.approach}
                          </Badge>
                        </div>
                        
                        {/* Strategy Details */}
                        <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                          <div>
                            <span className="text-muted-foreground">Suggested Price:</span>
                            <p className="font-medium">{strategy.suggested_price}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Margin Impact:</span>
                            <p className="font-medium">{strategy.margin_impact}</p>
                          </div>
                        </div>
                        
                        {/* Reasoning */}
                        {strategy.reasoning && (
                          <p className="text-xs text-muted-foreground mb-3 italic">
                            💡 {strategy.reasoning}
                          </p>
                        )}
                        
                        {/* Draft Message Preview */}
                        <div className="bg-white rounded p-3 border text-sm mb-3 whitespace-pre-wrap max-h-32 overflow-y-auto">
                          {strategy.draft_message}
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => approveAndSendResponse(strategy.draft_message)}
                            disabled={isApprovingResponse}
                            className="gap-1"
                            data-testid={`send-${strategy.name.toLowerCase()}-btn`}
                          >
                            {isApprovingResponse ? (
                              <><Loader2 className="h-3 w-3 animate-spin" /> Sending...</>
                            ) : (
                              <><CheckCircle className="h-3 w-3" /> Send This</>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyToMessageBox(strategy.draft_message)}
                            className="gap-1"
                            data-testid={`edit-${strategy.name.toLowerCase()}-btn`}
                          >
                            <ArrowRight className="h-3 w-3" /> Edit First
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Multiple Response Options (Alternative Format) */}
                {negotiationSuggestions.recommended_responses?.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-purple-600" />
                      Response Options
                      <Badge variant="outline" className="text-xs capitalize">{replyStyle} tone</Badge>
                    </h4>
                    {negotiationSuggestions.recommended_responses.map((resp, idx) => (
                      <div key={idx} className={`rounded-lg p-4 border ${
                        resp.type === 'accept' ? 'bg-green-50 border-green-200' :
                        resp.type === 'counter' ? 'bg-amber-50 border-amber-200' :
                        resp.type === 'decline' ? 'bg-red-50 border-red-200' :
                        'bg-purple-50 border-purple-200'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm">{resp.title}</span>
                          <Badge variant="outline" className="text-xs capitalize">
                            {resp.type}
                          </Badge>
                        </div>
                        <div className="bg-white rounded p-3 border text-sm mb-3 whitespace-pre-wrap">
                          {resp.message}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => approveAndSendResponse(resp.message)}
                            disabled={isApprovingResponse}
                            className="gap-1"
                            data-testid={`approve-${resp.type}-btn`}
                          >
                            {isApprovingResponse ? (
                              <><Loader2 className="h-3 w-3 animate-spin" /> Sending...</>
                            ) : (
                              <><CheckCircle className="h-3 w-3" /> Send This</>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyToMessageBox(resp.message)}
                            className="gap-1"
                            data-testid={`edit-${resp.type}-btn`}
                          >
                            <ArrowRight className="h-3 w-3" /> Edit First
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Legacy Single Response (backwards compatibility) */}
                {negotiationSuggestions.recommended_response && !negotiationSuggestions.recommended_responses && (
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                      <Sparkles className="h-4 w-4 text-purple-600" />
                      Suggested Response
                      <Badge variant="outline" className="text-xs">
                        {negotiationSuggestions.recommended_response.tone}
                      </Badge>
                    </h4>
                    <div className="bg-white rounded p-3 border text-sm mb-3 whitespace-pre-wrap">
                      {negotiationSuggestions.recommended_response.message}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => approveAndSendResponse(negotiationSuggestions.recommended_response.message)}
                        disabled={isApprovingResponse}
                        className="gap-1"
                        data-testid="approve-response-btn"
                      >
                        {isApprovingResponse ? (
                          <><Loader2 className="h-3 w-3 animate-spin" /> Sending...</>
                        ) : (
                          <><CheckCircle className="h-3 w-3" /> Approve & Send</>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyToMessageBox(negotiationSuggestions.recommended_response.message)}
                        className="gap-1"
                        data-testid="use-as-draft-btn"
                      >
                        <ArrowRight className="h-3 w-3" /> Edit First
                      </Button>
                    </div>
                  </div>
                )}

                {/* Next Steps */}
                {negotiationSuggestions.next_steps?.length > 0 && (
                  <div className="text-sm">
                    <h4 className="font-semibold mb-2">Next Steps</h4>
                    <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                      {negotiationSuggestions.next_steps.map((step, idx) => (
                        <li key={idx}>{step}</li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            ) : (
              <div className="py-8 text-center text-muted-foreground">
                <Brain className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>Click "AI Negotiation Assist" to get market-based suggestions</p>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNegotiationAssist(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* AI Controlled Draft Dialog */}
        <Dialog open={showControlledDraft} onOpenChange={setShowControlledDraft}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit3 className="h-5 w-5 text-blue-600" />
                AI Controlled Draft
              </DialogTitle>
              <DialogDescription>
                Generate qualification-first responses with controlled language
              </DialogDescription>
            </DialogHeader>
            
            <AIControlledDraft
              inquiryId={selectedInquiry?.id}
              productId={selectedInquiry?.product_id}
              inquiryMessage={selectedInquiry?.messages?.slice(-1)[0]?.message || selectedInquiry?.message || ''}
              conversationHistory={selectedInquiry?.messages || []}
              isFirstResponse={!selectedInquiry?.messages || selectedInquiry.messages.length <= 1}
              onDraftApproved={async (message) => {
                setNewMessage(message);
                setShowControlledDraft(false);
                // Auto-send if user wants
                await handleSendMessage({ preventDefault: () => {} });
              }}
              onClose={() => setShowControlledDraft(false)}
            />
          </DialogContent>
        </Dialog>

        {/* AI Negotiation Bands Dialog */}
        <Dialog open={showNegotiationAssist && aiMode === 'negotiate'} onOpenChange={(open) => {
          if (!open) {
            setShowNegotiationAssist(false);
            setAiMode('draft');
          }
        }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-purple-600" />
                Internal Negotiation Bands
              </DialogTitle>
              <DialogDescription>
                Set your pricing boundaries and get AI-powered strategy options
              </DialogDescription>
            </DialogHeader>
            
            <NegotiationBands
              inquiryId={selectedInquiry?.id}
              productId={selectedInquiry?.product_id}
              conversationHistory={selectedInquiry?.messages || []}
              productPrice={selectedInquiry?.product_price || 100}
              productUnit={selectedInquiry?.product_unit || 'unit'}
              onStrategySelected={(strategy) => {
                // Copy draft opening to message box
                setNewMessage(strategy.draft_opening);
                setShowNegotiationAssist(false);
                setAiMode('draft');
                toast.success(`${strategy.name} strategy copied to message box. Edit before sending!`);
              }}
              onClose={() => {
                setShowNegotiationAssist(false);
                setAiMode('draft');
              }}
            />
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
